// yard/DogInstance.jsx - Procedural low-poly dog with animation states
import React, { useRef, useMemo, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { Html } from '@react-three/drei';

// Dog coat color palettes - natural dog colors
const DOG_COATS = [
  { body: '#c4956a', belly: '#e8d5b7', ears: '#a07850', nose: '#2a1f1a' }, // Golden
  { body: '#8B6F47', belly: '#c4a67a', ears: '#6b5030', nose: '#1a1210' }, // Brown
  { body: '#f5e6d0', belly: '#ffffff', ears: '#e0c8a8', nose: '#3a2a20' }, // Cream
  { body: '#4a4a4a', belly: '#7a7a7a', ears: '#333333', nose: '#1a1a1a' }, // Dark Gray
  { body: '#d4a060', belly: '#f0dcc0', ears: '#b08040', nose: '#2a1a10' }, // Tan
  { body: '#8c5e3c', belly: '#c49a6c', ears: '#6a3e20', nose: '#1a0e08' }, // Chocolate
  { body: '#e8d8c8', belly: '#f8f0e8', ears: '#c8b098', nose: '#3a2820' }, // Light Tan
  { body: '#b0b0b0', belly: '#d8d8d8', ears: '#888888', nose: '#2a2a2a' }, // Silver
  { body: '#d49050', belly: '#ecc898', ears: '#aa6830', nose: '#201008' }, // Caramel
  { body: '#6e4e30', belly: '#a48060', ears: '#4e3018', nose: '#150a04' }, // Dark Brown
];

const GRAY = new THREE.Color('#777777');

const DogInstance = ({ dog, onClick, showLabel = false, dimmed = false, lod = 'high' }) => {
  const groupRef = useRef();
  const bodyRef = useRef();
  const headRef = useRef();
  const tailRef = useRef();
  const legFLRef = useRef();
  const legFRRef = useRef();
  const legBLRef = useRef();
  const legBRRef = useRef();
  const [hovered, setHovered] = useState(false);

  const animTime = useRef(0);

  // Pick a coat color based on dog ID for variety
  const coat = useMemo(() => {
    const idStr = String(dog.id);
    const hash = (idStr.charCodeAt(0) || 0) + (idStr.charCodeAt(1) || 0) + (idStr.charCodeAt(2) || 0);
    return DOG_COATS[hash % DOG_COATS.length];
  }, [dog.id]);

  // State-based color: STALE dogs slightly desaturated, COLD dogs more gray
  const bodyColor = useMemo(() => {
    const c = new THREE.Color(coat.body);
    if (dog.dealState === 'COLD') c.lerp(GRAY, 0.45);
    else if (dog.dealState === 'STALE') c.lerp(GRAY, 0.2);
    return c;
  }, [coat.body, dog.dealState]);

  const bellyColor = useMemo(() => {
    const c = new THREE.Color(coat.belly);
    if (dog.dealState === 'COLD') c.lerp(GRAY, 0.35);
    else if (dog.dealState === 'STALE') c.lerp(GRAY, 0.15);
    return c;
  }, [coat.belly, dog.dealState]);

  const earColor = useMemo(() => {
    const c = new THREE.Color(coat.ears);
    if (dog.dealState === 'COLD') c.lerp(GRAY, 0.4);
    else if (dog.dealState === 'STALE') c.lerp(GRAY, 0.18);
    return c;
  }, [coat.ears, dog.dealState]);

  const noseColor = useMemo(() => new THREE.Color(coat.nose), [coat.nose]);

  // Breed variations
  const breed = useMemo(() => {
    const idStr = String(dog.id);
    const hash = (idStr.charCodeAt(0) || 0) + (idStr.charCodeAt(1) || 0);
    const breeds = [
      { earType: 'floppy', snoutLength: 0.35, tailCurl: 0.3 },
      { earType: 'pointed', snoutLength: 0.4, tailCurl: 0.5 },
      { earType: 'floppy', snoutLength: 0.25, tailCurl: 0.7 },
      { earType: 'pointed', snoutLength: 0.3, tailCurl: 0.4 },
    ];
    return breeds[hash % breeds.length];
  }, [dog.id]);

  // Rest variant for COLD dogs
  const restVariant = useMemo(() => {
    const hash = String(dog.id).charCodeAt(0) || 0;
    return hash % 3;
  }, [dog.id]);

  // Health ring color
  const ringColor = dog.dealState === 'ACTIVE' ? '#10b981' :
                    dog.dealState === 'STALE' ? '#f59e0b' : '#ef4444';

  const materialOpacity = dimmed ? 0.3 : 1.0;
  const shouldCastShadow = lod === 'high' && !dimmed;

  // ─── GEOMETRY CONSTANTS ───
  // Body capsule: r=0.20, extends along Z. XY cross-section circle r=0.20.
  // Legs at X=±0.24 → clearly outside body radius.
  // Body center at Y=0.55. Body bottom at Y=0.55-0.20=0.35.
  // Legs at Y=0.34, paw bottom at ~Y=0.

  // Animation loop
  useFrame((state, delta) => {
    if (!groupRef.current) return;
    animTime.current += delta;
    const t = animTime.current;

    // Smooth rotation
    const diff = dog.rotation - groupRef.current.rotation.y;
    groupRef.current.rotation.y += diff * Math.min(delta * 5, 1);

    if (lod === 'low') return;

    const anim = dog.animation;

    // Head/tail are CHILDREN of bodyRef. Y values are LOCAL offsets.
    // headRef base: [0, 0.16, 0.50]
    // tailRef base: [0, 0.06, -0.48]

    if (anim === 'run' || anim === 'walk') {
      const speed = anim === 'run' ? 12 : 6;
      const amplitude = anim === 'run' ? 0.4 : 0.2;

      if (bodyRef.current) {
        bodyRef.current.position.y = 0.55 + Math.abs(Math.sin(t * speed)) * 0.08;
        bodyRef.current.rotation.x = Math.sin(t * speed) * 0.04;
        bodyRef.current.rotation.z = 0;
        bodyRef.current.scale.set(1, 1, 1);
      }
      if (headRef.current) {
        headRef.current.position.y = 0.16;
        headRef.current.rotation.x = Math.sin(t * speed * 0.5) * 0.08;
        headRef.current.rotation.y = 0;
      }
      if (legFLRef.current) { legFLRef.current.rotation.x = Math.sin(t * speed) * amplitude; legFLRef.current.position.y = 0; }
      if (legBRRef.current) { legBRRef.current.rotation.x = Math.sin(t * speed) * amplitude; legBRRef.current.position.y = 0; }
      if (legFRRef.current) { legFRRef.current.rotation.x = Math.sin(t * speed + Math.PI) * amplitude; legFRRef.current.position.y = 0; }
      if (legBLRef.current) { legBLRef.current.rotation.x = Math.sin(t * speed + Math.PI) * amplitude; legBLRef.current.position.y = 0; }
      if (tailRef.current) {
        tailRef.current.rotation.z = Math.sin(t * 15) * 0.5;
        tailRef.current.rotation.x = -0.3 + Math.sin(t * speed) * 0.1;
      }
    } else if (anim === 'sit') {
      if (bodyRef.current) {
        bodyRef.current.position.y = 0.40;
        bodyRef.current.rotation.x = -0.15;
        bodyRef.current.rotation.z = 0;
        bodyRef.current.scale.set(1, 1, 1);
      }
      if (headRef.current) {
        headRef.current.position.y = 0.24;
        headRef.current.rotation.x = Math.sin(t * 2) * 0.05;
        headRef.current.rotation.y = Math.sin(t * 1.2) * 0.08;
      }
      if (legFLRef.current) { legFLRef.current.rotation.x = 0; legFLRef.current.position.y = 0; }
      if (legFRRef.current) { legFRRef.current.rotation.x = 0; legFRRef.current.position.y = 0; }
      if (legBLRef.current) { legBLRef.current.rotation.x = 0.7; legBLRef.current.position.y = 0; }
      if (legBRRef.current) { legBRRef.current.rotation.x = 0.7; legBRRef.current.position.y = 0; }
      if (tailRef.current) {
        tailRef.current.rotation.z = Math.sin(t * 3) * 0.2;
        tailRef.current.rotation.x = 0.2;
      }
    } else if (anim === 'lay') {
      // Resting poses for COLD dogs — body lowers, legs tuck
      if (restVariant === 1) {
        // Curled up
        if (bodyRef.current) {
          bodyRef.current.position.y = 0.24;
          bodyRef.current.rotation.x = 0;
          bodyRef.current.rotation.z = 0.12;
        }
        if (headRef.current) {
          headRef.current.position.y = 0.10;
          headRef.current.rotation.x = 0.15;
          headRef.current.rotation.y = Math.sin(t * 0.5) * 0.05;
        }
        if (legFLRef.current) { legFLRef.current.rotation.x = -0.3; legFLRef.current.position.y = -0.10; }
        if (legFRRef.current) { legFRRef.current.rotation.x = -0.2; legFRRef.current.position.y = -0.10; }
        if (legBLRef.current) { legBLRef.current.rotation.x = 1.0; legBLRef.current.position.y = -0.10; }
        if (legBRRef.current) { legBRRef.current.rotation.x = 1.0; legBRRef.current.position.y = -0.10; }
        if (tailRef.current) { tailRef.current.rotation.z = 0.4; tailRef.current.rotation.x = 0.8; }
      } else if (restVariant === 2) {
        // Head on paws
        if (bodyRef.current) {
          bodyRef.current.position.y = 0.26;
          bodyRef.current.rotation.x = 0.06;
          bodyRef.current.rotation.z = 0;
        }
        if (headRef.current) {
          headRef.current.position.y = 0.02;
          headRef.current.rotation.x = 0.25;
          headRef.current.rotation.y = Math.sin(t * 0.3) * 0.03;
        }
        if (legFLRef.current) { legFLRef.current.rotation.x = -0.6; legFLRef.current.position.y = -0.12; }
        if (legFRRef.current) { legFRRef.current.rotation.x = -0.6; legFRRef.current.position.y = -0.12; }
        if (legBLRef.current) { legBLRef.current.rotation.x = 1.2; legBLRef.current.position.y = -0.12; }
        if (legBRRef.current) { legBRRef.current.rotation.x = 1.2; legBRRef.current.position.y = -0.12; }
        if (tailRef.current) { tailRef.current.rotation.z = Math.sin(t * 0.8) * 0.03; tailRef.current.rotation.x = 0.6; }
      } else {
        // Sphinx pose
        if (bodyRef.current) {
          bodyRef.current.position.y = 0.26;
          bodyRef.current.rotation.x = 0.04;
          bodyRef.current.rotation.z = 0;
        }
        if (headRef.current) {
          headRef.current.position.y = 0.18;
          headRef.current.rotation.x = -0.08 + Math.sin(t * 1.5) * 0.03;
          headRef.current.rotation.y = Math.sin(t * 0.7) * 0.1;
        }
        if (legFLRef.current) { legFLRef.current.rotation.x = -0.5; legFLRef.current.position.y = -0.10; }
        if (legFRRef.current) { legFRRef.current.rotation.x = -0.5; legFRRef.current.position.y = -0.10; }
        if (legBLRef.current) { legBLRef.current.rotation.x = 1.2; legBLRef.current.position.y = -0.10; }
        if (legBRRef.current) { legBRRef.current.rotation.x = 1.2; legBRRef.current.position.y = -0.10; }
        if (tailRef.current) { tailRef.current.rotation.z = Math.sin(t * 1) * 0.05; tailRef.current.rotation.x = 0.8; }
      }
      // Breathing
      if (bodyRef.current) {
        bodyRef.current.scale.x = 1 + Math.sin(t * 2) * 0.015;
        bodyRef.current.scale.z = 1 + Math.sin(t * 2) * 0.015;
      }
    } else {
      // Idle
      if (bodyRef.current) {
        bodyRef.current.position.y = 0.55;
        bodyRef.current.rotation.x = 0;
        bodyRef.current.rotation.z = 0;
        bodyRef.current.scale.set(1, 1, 1);
      }
      if (headRef.current) {
        headRef.current.position.y = 0.16;
        headRef.current.rotation.x = Math.sin(t * 1.5) * 0.08;
        headRef.current.rotation.y = Math.sin(t * 0.8) * 0.12;
      }
      if (tailRef.current) {
        tailRef.current.rotation.z = Math.sin(t * 4) * 0.3;
      }
      if (legFLRef.current) { legFLRef.current.rotation.x = 0; legFLRef.current.position.y = 0; }
      if (legFRRef.current) { legFRRef.current.rotation.x = 0; legFRRef.current.position.y = 0; }
      if (legBLRef.current) { legBLRef.current.rotation.x = 0; legBLRef.current.position.y = 0; }
      if (legBRRef.current) { legBRRef.current.rotation.x = 0; legBRRef.current.position.y = 0; }
    }
  });

  const pointerHandlers = {
    onClick: (e) => { e.stopPropagation(); onClick && onClick(dog); },
    onPointerOver: (e) => { e.stopPropagation(); setHovered(true); document.body.style.cursor = 'pointer'; },
    onPointerOut: () => { setHovered(false); document.body.style.cursor = 'auto'; },
  };

  // Low LOD: simplified rendering for distant dogs
  if (lod === 'low') {
    return (
      <group ref={groupRef} position={[dog.position.x, dog.position.y, dog.position.z]} rotation={[0, dog.rotation, 0]} scale={dog.scale} {...pointerHandlers}>
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}>
          <ringGeometry args={[0.8, 1, 16]} />
          <meshBasicMaterial color={ringColor} transparent opacity={dimmed ? 0.1 : 0.4} />
        </mesh>
        <mesh position={[0, 0.40, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <capsuleGeometry args={[0.20, 0.7, 4, 8]} />
          <meshStandardMaterial color={bodyColor} roughness={0.7} transparent={dimmed} opacity={materialOpacity} />
        </mesh>
        {/* Simple legs for low LOD */}
        <mesh position={[0.20, 0.15, 0.22]}>
          <capsuleGeometry args={[0.06, 0.2, 3, 4]} />
          <meshStandardMaterial color={bodyColor} roughness={0.7} transparent={dimmed} opacity={materialOpacity} />
        </mesh>
        <mesh position={[-0.20, 0.15, 0.22]}>
          <capsuleGeometry args={[0.06, 0.2, 3, 4]} />
          <meshStandardMaterial color={bodyColor} roughness={0.7} transparent={dimmed} opacity={materialOpacity} />
        </mesh>
        <mesh position={[0.20, 0.15, -0.22]}>
          <capsuleGeometry args={[0.06, 0.2, 3, 4]} />
          <meshStandardMaterial color={bodyColor} roughness={0.7} transparent={dimmed} opacity={materialOpacity} />
        </mesh>
        <mesh position={[-0.20, 0.15, -0.22]}>
          <capsuleGeometry args={[0.06, 0.2, 3, 4]} />
          <meshStandardMaterial color={bodyColor} roughness={0.7} transparent={dimmed} opacity={materialOpacity} />
        </mesh>
        {hovered && dog.deal && (
          <Html position={[0, 2, 0]} center distanceFactor={15} style={{ pointerEvents: 'none', userSelect: 'none', zIndex: 1000 }}>
            <div style={{ background: 'rgba(10, 102, 194, 0.95)', color: '#fff', padding: '5px 10px', borderRadius: 6, fontSize: 11, whiteSpace: 'nowrap', fontFamily: 'system-ui, sans-serif', boxShadow: '0 4px 12px rgba(0,0,0,0.4)' }}>
              <div style={{ fontWeight: 600 }}>{dog.deal.title || dog.deal.JobTitle || 'Deal'}</div>
              <div style={{ opacity: 0.8 }}>${(dog.deal.value || dog.deal.fee || 0).toLocaleString()}</div>
            </div>
          </Html>
        )}
      </group>
    );
  }

  return (
    <group ref={groupRef} position={[dog.position.x, dog.position.y, dog.position.z]} rotation={[0, dog.rotation, 0]} scale={dog.scale} {...pointerHandlers}>

      {/* Activity ring */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}>
        <ringGeometry args={[0.8, 1, 32]} />
        <meshBasicMaterial color={ringColor} transparent opacity={dimmed ? 0.1 : (dog.eventState === 'CHASE_SQUIRREL' ? 0.8 : 0.4)} />
      </mesh>

      {/* ===== BODY GROUP (head + tail are children, move with body) ===== */}
      <group ref={bodyRef} position={[0, 0.55, 0]}>
        {/* Main body - capsule r=0.20 extending along Z */}
        <mesh castShadow={shouldCastShadow} rotation={[Math.PI / 2, 0, 0]}>
          <capsuleGeometry args={[0.20, 0.80, 6, 12]} />
          <meshStandardMaterial color={bodyColor} roughness={0.7} transparent={dimmed} opacity={materialOpacity} depthWrite />
        </mesh>
        {/* Belly - lighter underside */}
        <mesh position={[0, -0.10, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <capsuleGeometry args={[0.15, 0.60, 4, 8]} />
          <meshStandardMaterial color={bellyColor} roughness={0.7} transparent={dimmed} opacity={materialOpacity} depthWrite />
        </mesh>
        {/* Chest tuft */}
        <mesh position={[0, -0.02, 0.42]}>
          <sphereGeometry args={[0.13, 8, 6]} />
          <meshStandardMaterial color={bellyColor} roughness={0.8} transparent={dimmed} opacity={materialOpacity} depthWrite />
        </mesh>

        {/* ===== HEAD (child of body) ===== */}
        <group ref={headRef} position={[0, 0.16, 0.50]} scale={0.85}>
          {/* Skull */}
          <mesh castShadow={shouldCastShadow}>
            <sphereGeometry args={[0.25, 10, 8]} />
            <meshStandardMaterial color={bodyColor} roughness={0.7} transparent={dimmed} opacity={materialOpacity} depthWrite />
          </mesh>

          {/* Snout */}
          <mesh position={[0, -0.08, 0.2 + breed.snoutLength * 0.3]} castShadow={shouldCastShadow}>
            <boxGeometry args={[0.2, 0.16, breed.snoutLength]} />
            <meshStandardMaterial color={bodyColor} roughness={0.7} transparent={dimmed} opacity={materialOpacity} depthWrite />
          </mesh>

          {/* Snout bridge */}
          <mesh position={[0, -0.02, 0.18 + breed.snoutLength * 0.2]}>
            <boxGeometry args={[0.16, 0.08, breed.snoutLength * 0.6]} />
            <meshStandardMaterial color={earColor} roughness={0.7} transparent={dimmed} opacity={materialOpacity} depthWrite />
          </mesh>

          {/* Nose */}
          <mesh position={[0, -0.06, 0.22 + breed.snoutLength * 0.55]}>
            <sphereGeometry args={[0.07, 8, 8]} />
            <meshStandardMaterial color={noseColor} roughness={0.15} metalness={0.1} transparent={dimmed} opacity={materialOpacity} depthWrite />
          </mesh>

          {/* Mouth line */}
          <mesh position={[0, -0.14, 0.2 + breed.snoutLength * 0.2]}>
            <boxGeometry args={[0.12, 0.015, breed.snoutLength * 0.4]} />
            <meshStandardMaterial color={noseColor} roughness={0.5} transparent={dimmed} opacity={materialOpacity} depthWrite />
          </mesh>

          {/* Eyes */}
          <mesh position={[0.1, 0.04, 0.18]}>
            <sphereGeometry args={[0.055, 8, 8]} />
            <meshStandardMaterial color="#1a1a1a" roughness={0.2} transparent={dimmed} opacity={materialOpacity} depthWrite />
          </mesh>
          <mesh position={[-0.1, 0.04, 0.18]}>
            <sphereGeometry args={[0.055, 8, 8]} />
            <meshStandardMaterial color="#1a1a1a" roughness={0.2} transparent={dimmed} opacity={materialOpacity} depthWrite />
          </mesh>

          {/* Eye highlights */}
          <mesh position={[0.12, 0.06, 0.2]}>
            <sphereGeometry args={[0.02, 6, 6]} />
            <meshBasicMaterial color="#ffffff" transparent opacity={dimmed ? 0.3 : 0.9} />
          </mesh>
          <mesh position={[-0.08, 0.06, 0.2]}>
            <sphereGeometry args={[0.02, 6, 6]} />
            <meshBasicMaterial color="#ffffff" transparent opacity={dimmed ? 0.3 : 0.9} />
          </mesh>

          {/* Eyebrows */}
          <mesh position={[0.1, 0.1, 0.16]}>
            <boxGeometry args={[0.08, 0.025, 0.04]} />
            <meshStandardMaterial color={earColor} roughness={0.8} transparent={dimmed} opacity={materialOpacity} depthWrite />
          </mesh>
          <mesh position={[-0.1, 0.1, 0.16]}>
            <boxGeometry args={[0.08, 0.025, 0.04]} />
            <meshStandardMaterial color={earColor} roughness={0.8} transparent={dimmed} opacity={materialOpacity} depthWrite />
          </mesh>

          {/* Ears */}
          {breed.earType === 'floppy' ? (
            <>
              <mesh position={[0.18, 0.12, -0.08]} rotation={[0.2, 0.15, 0.5]} castShadow={shouldCastShadow}>
                <boxGeometry args={[0.12, 0.28, 0.06]} />
                <meshStandardMaterial color={earColor} roughness={0.8} transparent={dimmed} opacity={materialOpacity} depthWrite />
              </mesh>
              <mesh position={[-0.18, 0.12, -0.08]} rotation={[0.2, -0.15, -0.5]} castShadow={shouldCastShadow}>
                <boxGeometry args={[0.12, 0.28, 0.06]} />
                <meshStandardMaterial color={earColor} roughness={0.8} transparent={dimmed} opacity={materialOpacity} depthWrite />
              </mesh>
            </>
          ) : (
            <>
              <mesh position={[0.13, 0.24, -0.02]} rotation={[-0.1, 0, -0.2]} castShadow={shouldCastShadow}>
                <coneGeometry args={[0.07, 0.2, 4]} />
                <meshStandardMaterial color={earColor} roughness={0.8} transparent={dimmed} opacity={materialOpacity} depthWrite />
              </mesh>
              <mesh position={[-0.13, 0.24, -0.02]} rotation={[-0.1, 0, 0.2]} castShadow={shouldCastShadow}>
                <coneGeometry args={[0.07, 0.2, 4]} />
                <meshStandardMaterial color={earColor} roughness={0.8} transparent={dimmed} opacity={materialOpacity} depthWrite />
              </mesh>
              {/* Inner ear (pink) */}
              <mesh position={[0.13, 0.22, -0.01]} rotation={[-0.1, 0, -0.2]}>
                <coneGeometry args={[0.04, 0.12, 4]} />
                <meshStandardMaterial color="#d4a0a0" roughness={0.6} transparent={dimmed} opacity={materialOpacity} depthWrite />
              </mesh>
              <mesh position={[-0.13, 0.22, -0.01]} rotation={[-0.1, 0, 0.2]}>
                <coneGeometry args={[0.04, 0.12, 4]} />
                <meshStandardMaterial color="#d4a0a0" roughness={0.6} transparent={dimmed} opacity={materialOpacity} depthWrite />
              </mesh>
            </>
          )}
        </group>

        {/* ===== TAIL (child of body) ===== */}
        <group ref={tailRef} position={[0, 0.06, -0.48]}>
          {/* Base */}
          <mesh position={[0, 0.1, -0.08]} rotation={[-0.5 - breed.tailCurl * 0.5, 0, 0]} castShadow={shouldCastShadow}>
            <capsuleGeometry args={[0.04, 0.18, 4, 6]} />
            <meshStandardMaterial color={bodyColor} roughness={0.7} transparent={dimmed} opacity={materialOpacity} depthWrite />
          </mesh>
          {/* Tip */}
          <mesh position={[0, 0.20, -0.16]} rotation={[-0.8 - breed.tailCurl * 0.3, 0, 0]} castShadow={shouldCastShadow}>
            <capsuleGeometry args={[0.03, 0.14, 4, 6]} />
            <meshStandardMaterial color={bodyColor} roughness={0.7} transparent={dimmed} opacity={materialOpacity} depthWrite />
          </mesh>
        </group>
      </group>{/* close body group */}

      {/* ===== LEGS ===== */}
      {/* Legs at X=±0.24, clearly outside body radius of 0.20 */}
      {/* Front Left */}
      <group ref={legFLRef} position={[0.24, 0.34, 0.30]}>
        <mesh position={[0, -0.04, 0]} castShadow={shouldCastShadow}>
          <capsuleGeometry args={[0.075, 0.14, 4, 6]} />
          <meshStandardMaterial color={bodyColor} roughness={0.7} transparent={dimmed} opacity={materialOpacity} depthWrite />
        </mesh>
        <mesh position={[0, -0.18, 0]} castShadow={shouldCastShadow}>
          <capsuleGeometry args={[0.065, 0.10, 4, 6]} />
          <meshStandardMaterial color={bodyColor} roughness={0.7} transparent={dimmed} opacity={materialOpacity} depthWrite />
        </mesh>
        <mesh position={[0, -0.28, 0.02]}>
          <sphereGeometry args={[0.08, 6, 4]} />
          <meshStandardMaterial color={bellyColor} roughness={0.6} transparent={dimmed} opacity={materialOpacity} depthWrite />
        </mesh>
      </group>

      {/* Front Right */}
      <group ref={legFRRef} position={[-0.24, 0.34, 0.30]}>
        <mesh position={[0, -0.04, 0]} castShadow={shouldCastShadow}>
          <capsuleGeometry args={[0.075, 0.14, 4, 6]} />
          <meshStandardMaterial color={bodyColor} roughness={0.7} transparent={dimmed} opacity={materialOpacity} depthWrite />
        </mesh>
        <mesh position={[0, -0.18, 0]} castShadow={shouldCastShadow}>
          <capsuleGeometry args={[0.065, 0.10, 4, 6]} />
          <meshStandardMaterial color={bodyColor} roughness={0.7} transparent={dimmed} opacity={materialOpacity} depthWrite />
        </mesh>
        <mesh position={[0, -0.28, 0.02]}>
          <sphereGeometry args={[0.08, 6, 4]} />
          <meshStandardMaterial color={bellyColor} roughness={0.6} transparent={dimmed} opacity={materialOpacity} depthWrite />
        </mesh>
      </group>

      {/* Back Left */}
      <group ref={legBLRef} position={[0.24, 0.34, -0.30]}>
        <mesh position={[0, -0.04, 0]} castShadow={shouldCastShadow}>
          <capsuleGeometry args={[0.08, 0.14, 4, 6]} />
          <meshStandardMaterial color={bodyColor} roughness={0.7} transparent={dimmed} opacity={materialOpacity} depthWrite />
        </mesh>
        <mesh position={[0, -0.18, 0]} castShadow={shouldCastShadow}>
          <capsuleGeometry args={[0.07, 0.10, 4, 6]} />
          <meshStandardMaterial color={bodyColor} roughness={0.7} transparent={dimmed} opacity={materialOpacity} depthWrite />
        </mesh>
        <mesh position={[0, -0.28, 0.02]}>
          <sphereGeometry args={[0.08, 6, 4]} />
          <meshStandardMaterial color={bellyColor} roughness={0.6} transparent={dimmed} opacity={materialOpacity} depthWrite />
        </mesh>
      </group>

      {/* Back Right */}
      <group ref={legBRRef} position={[-0.24, 0.34, -0.30]}>
        <mesh position={[0, -0.04, 0]} castShadow={shouldCastShadow}>
          <capsuleGeometry args={[0.08, 0.14, 4, 6]} />
          <meshStandardMaterial color={bodyColor} roughness={0.7} transparent={dimmed} opacity={materialOpacity} depthWrite />
        </mesh>
        <mesh position={[0, -0.18, 0]} castShadow={shouldCastShadow}>
          <capsuleGeometry args={[0.07, 0.10, 4, 6]} />
          <meshStandardMaterial color={bodyColor} roughness={0.7} transparent={dimmed} opacity={materialOpacity} depthWrite />
        </mesh>
        <mesh position={[0, -0.28, 0.02]}>
          <sphereGeometry args={[0.08, 6, 4]} />
          <meshStandardMaterial color={bellyColor} roughness={0.6} transparent={dimmed} opacity={materialOpacity} depthWrite />
        </mesh>
      </group>

      {/* ===== LABEL ===== */}
      {(showLabel || hovered) && dog.deal && (
        <Html
          position={[0, 2, 0]}
          center
          distanceFactor={15}
          style={{ pointerEvents: 'none', userSelect: 'none', zIndex: hovered ? 1000 : 1 }}
        >
          <div style={{
            background: hovered ? 'rgba(10, 102, 194, 0.95)' : 'rgba(0,0,0,0.85)',
            color: '#fff',
            padding: hovered ? '6px 12px' : '4px 8px',
            borderRadius: hovered ? 8 : 6,
            fontSize: hovered ? 12 : 10,
            whiteSpace: 'nowrap',
            fontFamily: 'system-ui, sans-serif',
            transform: hovered ? 'scale(1.1)' : 'scale(1)',
            transition: 'all 0.15s ease',
            boxShadow: hovered ? '0 4px 16px rgba(0,0,0,0.5)' : '0 2px 4px rgba(0,0,0,0.3)',
            borderLeft: `3px solid ${ringColor}`,
          }}>
            <div style={{ fontWeight: 700, letterSpacing: '0.01em' }}>{dog.deal.title || dog.deal.JobTitle || 'Deal'}</div>
            <div style={{ opacity: 0.8, fontSize: hovered ? 11 : 9 }}>${(dog.deal.value || dog.deal.fee || 0).toLocaleString()}</div>
          </div>
        </Html>
      )}
    </group>
  );
};

export default DogInstance;
